(() => {
  'use strict';

  const context = window.SKW_CLARITY_CONTEXT || {};
  const queueCall = (...args) => {
    if (typeof window.clarity === 'function') {
      window.clarity(...args);
      return true;
    }
    return false;
  };

  const whenClarityReady = (callback, attempts = 0) => {
    if (typeof window.clarity === 'function') {
      callback();
      return;
    }
    if (attempts < 40) window.setTimeout(() => whenClarityReady(callback, attempts + 1), 250);
  };

  const safeValue = (value) => {
    if (value === undefined || value === null || value === '') return 'unknown';
    return String(value).slice(0, 128);
  };

  const getSessionId = () => {
    const key = 'skw_clarity_session_id';
    try {
      let value = window.sessionStorage.getItem(key);
      if (!value) {
        value = window.crypto?.randomUUID?.() || `skw-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        window.sessionStorage.setItem(key, value);
      }
      return value;
    } catch (_) {
      return `skw-${Date.now()}`;
    }
  };

  const getCampaign = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get('utm_source') || context.utmSource || 'none',
      medium: params.get('utm_medium') || context.utmMedium || 'none',
      campaign: params.get('utm_campaign') || context.utmCampaign || 'none',
      content: params.get('utm_content') || context.utmContent || 'none'
    };
  };

  const tag = (key, value) => queueCall('set', key, safeValue(value));
  const upgrade = (reason) => queueCall('upgrade', safeValue(reason));

  const applyContext = () => {
    const campaign = getCampaign();
    const pageId = `${context.pageType || 'page'}:${context.resourceHandle || window.location.pathname}`;
    const sessionId = getSessionId();

    if (context.customerId) {
      queueCall('identify', context.customerId, sessionId, pageId, context.customerLabel || context.customerId);
    } else {
      queueCall('identify', null, sessionId, pageId, 'guest');
    }

    tag('language', context.language);
    tag('country', context.country);
    tag('currency', context.currency);
    tag('page_type', context.pageType);
    tag('resource_type', context.resourceType);
    tag('resource_handle', context.resourceHandle);
    tag('content_cluster', context.contentCluster);
    tag('season', context.season);
    tag('product_type', context.productType);
    tag('collection', context.collectionHandle);
    tag('blog', context.blogHandle);
    tag('logged_in', context.customerId ? 'yes' : 'no');
    tag('utm_source', campaign.source);
    tag('utm_medium', campaign.medium);
    tag('utm_campaign', campaign.campaign);
    tag('utm_content', campaign.content);
    tag('referrer_host', document.referrer ? new URL(document.referrer).hostname : 'direct');

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'skw_clarity_context',
      clarity_page_type: context.pageType,
      clarity_content_cluster: context.contentCluster,
      clarity_season: context.season,
      clarity_language: context.language,
      clarity_product_type: context.productType,
      clarity_collection: context.collectionHandle,
      clarity_utm_source: campaign.source,
      clarity_utm_medium: campaign.medium,
      clarity_utm_campaign: campaign.campaign
    });

    if (context.pageType === 'cart') upgrade('cart_view');
  };

  const isAddToCartForm = (form) => {
    const action = form?.getAttribute('action') || '';
    return action.includes('/cart/add');
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (isAddToCartForm(form)) upgrade('add_to_cart');

    const action = (form.getAttribute('action') || '').toLowerCase();
    const formClass = form.className?.toString().toLowerCase() || '';
    if (action.includes('/contact') || formClass.includes('contact')) upgrade('contact_form');
    if (formClass.includes('newsletter') || form.querySelector('input[name="contact[email]"]')) upgrade('newsletter_signup');
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('a, button, input[type="submit"]') : null;
    if (!target) return;

    const href = target.getAttribute('href') || '';
    const name = (target.getAttribute('name') || '').toLowerCase();
    const text = (target.textContent || target.getAttribute('value') || '').trim().toLowerCase();

    if (href.includes('/checkout') || name === 'checkout' || text.includes('checkout') || text.includes('zur kasse')) {
      upgrade('checkout_start');
    }
    if (href.includes('/cart') || text.includes('warenkorb')) upgrade('cart_open');
  }, true);

  const originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    window.fetch = function (...args) {
      const url = String(args[0]?.url || args[0] || '');
      const result = originalFetch.apply(this, args);
      if (url.includes('/cart/add')) upgrade('add_to_cart');
      if (url.includes('/cart/change') || url.includes('/cart/update')) upgrade('cart_update');
      return result;
    };
  }

  whenClarityReady(applyContext);
})();

(() => {
  if (window.skwPersonalizationOrderNoteReady) return;
  window.skwPersonalizationOrderNoteReady = true;

  const START_MARKER = '=== SKW PERSONALISIERUNG ===';
  const END_MARKER = '=== ENDE SKW PERSONALISIERUNG ===';
  let syncQueue = Promise.resolve();
  let scheduledSync = null;
  let personalizationState = 'unknown';

  const cleanLine = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

  const stripManagedBlocks = (note) => {
    let result = String(note ?? '');
    let start = result.indexOf(START_MARKER);

    while (start !== -1) {
      const end = result.indexOf(END_MARKER, start + START_MARKER.length);
      if (end === -1) {
        result = result.slice(0, start);
        break;
      }
      result = `${result.slice(0, start)}${result.slice(end + END_MARKER.length)}`;
      start = result.indexOf(START_MARKER);
    }

    return result.replace(/\n{3,}/g, '\n\n').trim();
  };

  const getPersonalization = (item) => {
    const properties = item?.properties;
    if (!properties) return '';

    if (Array.isArray(properties)) {
      const match = properties.find((entry) => {
        if (Array.isArray(entry)) return entry[0] === 'Personalisierung';
        return entry?.name === 'Personalisierung' || entry?.key === 'Personalisierung';
      });
      if (Array.isArray(match)) return cleanLine(match[1]);
      return cleanLine(match?.value);
    }

    return cleanLine(properties.Personalisierung);
  };

  const hasPersonalization = (items) =>
    (items || []).some((item) => Boolean(getPersonalization(item)));

  const buildManagedBlock = (items) => {
    const entries = items
      .map((item) => {
        const personalization = getPersonalization(item);
        if (!personalization) return null;

        const lines = [
          `Lieferant: ${cleanLine(item.vendor) || 'nicht angegeben'}`,
          `Artikel: ${cleanLine(item.product_title || item.title) || 'nicht angegeben'}`,
        ];

        const variantTitle = cleanLine(item.variant_title);
        if (variantTitle && variantTitle.toLowerCase() !== 'default title') {
          lines.push(`Variante: ${variantTitle}`);
        }

        lines.push(`SKU: ${cleanLine(item.sku) || 'nicht angegeben'}`);
        lines.push(`Menge: ${Number(item.quantity) || 1}`);
        lines.push(`Personalisierung: ${personalization}`);
        return lines.join('\n');
      })
      .filter(Boolean);

    if (!entries.length) return '';
    return `${START_MARKER}\n${entries.join('\n\n')}\n${END_MARKER}`;
  };

  const updateVisibleNoteFields = (note) => {
    document.querySelectorAll('textarea[name="note"]').forEach((field) => {
      if (field.value !== note) field.value = note;
    });
  };

  const performSync = async () => {
    const cartUrl = window.routes?.cart_url || '/cart';
    const updateUrl = window.routes?.cart_update_url || '/cart/update.js';

    const cartResponse = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!cartResponse.ok) throw new Error(`Cart read failed (${cartResponse.status})`);

    const cart = await cartResponse.json();
    const items = cart.items || [];
    personalizationState = hasPersonalization(items) ? 'present' : 'absent';

    const customerNote = stripManagedBlocks(cart.note);
    const managedBlock = buildManagedBlock(items);
    const nextNote = [customerNote, managedBlock].filter(Boolean).join('\n\n');
    const currentNote = String(cart.note ?? '').trim();

    if (currentNote === nextNote.trim()) {
      updateVisibleNoteFields(nextNote);
      return cart;
    }

    const updateResponse = await fetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ note: nextNote }),
    });
    if (!updateResponse.ok) throw new Error(`Cart note update failed (${updateResponse.status})`);

    const updatedCart = await updateResponse.json();
    if (updatedCart?.errors) throw new Error(updatedCart.errors);

    updateVisibleNoteFields(nextNote);
    window.setTimeout(() => updateVisibleNoteFields(nextNote), 150);
    return updatedCart;
  };

  window.skwSyncPersonalizationOrderNote = () => {
    syncQueue = syncQueue.catch(() => null).then(performSync);
    return syncQueue;
  };

  const scheduleSync = () => {
    window.clearTimeout(scheduledSync);
    scheduledSync = window.setTimeout(() => {
      window.skwSyncPersonalizationOrderNote().catch((error) => {
        personalizationState = 'unknown';
        console.error('[SKW] Personalisierungs-Bestellnotiz konnte nicht synchronisiert werden.', error);
      });
    }, 120);
  };

  const warmCartState = () => {
    if (!document.querySelector('[name="checkout"], form[action*="/cart"]')) return;
    window.skwSyncPersonalizationOrderNote().catch((error) => {
      personalizationState = 'unknown';
      console.error('[SKW] Initiale Prüfung der Personalisierungs-Bestellnotiz fehlgeschlagen.', error);
    });
  };

  const bindCartUpdates = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleSync);
    }
    warmCartState();
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const submitter = event.submitter;
      if (!(form instanceof HTMLFormElement) || submitter?.name !== 'checkout') return;
      if (form.dataset.skwPersonalizationNoteSynced === 'true') return;

      // Normal carts keep Shopify's native checkout path completely untouched.
      if (personalizationState !== 'present') return;

      event.preventDefault();
      submitter.disabled = true;

      window.skwSyncPersonalizationOrderNote()
        .catch((error) => {
          console.error('[SKW] Letzte Synchronisierung der Personalisierungs-Bestellnotiz fehlgeschlagen.', error);
        })
        .finally(() => {
          form.dataset.skwPersonalizationNoteSynced = 'true';
          submitter.disabled = false;
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
          else form.submit();
          window.setTimeout(() => delete form.dataset.skwPersonalizationNoteSynced, 1000);
        });
    },
    true,
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCartUpdates, { once: true });
  } else {
    bindCartUpdates();
  }
})();
